// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { injectCss } from "@insite/mobius/utilities";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import { getSettingsCollection } from "@keller/App/Store/Selectors/SettingsSelectors";
import { ellipsis } from "@keller/XDL/Utilities";
import parseImageUrl from "@insite/mobius/utilities/parseImageUrl";
import React from "react";
import styled from "styled-components";

interface ImagePresentationProps {
    /** CSS string or styled-components function to be injected into this component.
     * @themable */
    css?: StyledProp<ImgProps>;
}

export type ImgProps = MobiusStyledComponentProps<
    "img",
    ImagePresentationProps & {
        /** The URL to fetch the image from. If not provided, Img renders `null`. */
        src?: string;
        /** The alternative text to display in place of the image. */
        altText?: string;
        /** Controls whether the img is lazy or eager loaded, default is eager */
        loading?: string;
        // Event handler for screen readers accessing an image modal in ProductPrimaryImage.tsx
        onKeyPress?: React.KeyboardEventHandler<HTMLImageElement>;
    }
>;

// XNG-Changes
const selectNotFoundImagePath = (state: any) => {
    const customSettings = getSettingsCollection(state).customSettings;
    return (
        customSettings.notFoundLargeImagePath ||
        customSettings.notFoundMediumImagePath ||
        customSettings.notFoundSmallImagePath
    );
};

function Img(props: ImgProps) {
    // XNG-Changes
    const notFoundImagePath = useAppSelector(selectNotFoundImagePath);

    const { src, altText, loading, ...otherProps } = props;

    // XNG-Changes: Inject width and height from style props
    const dimensions = useQueryStringsDimensions(src, {
        width: otherProps.width || "",
        height: otherProps.height || "",
    });

    if (!src) {
        return null;
    }

    return (
        <StyledImg
            {...otherProps}
            loading={loading || "lazy"}
            src={dimensions.url}
            alt={altText}
            width={dimensions.width || otherProps.width}
            height={dimensions.height || otherProps.height}
            onError={(event: any) => {
                const { currentTarget } = event;
                if (
                    currentTarget.src &&
                    (!notFoundImagePath || currentTarget.src.lastIndexOf(notFoundImagePath) > -1)
                ) {
                    otherProps.onError?.(event);
                    return;
                }
                currentTarget.onerror = null;
                currentTarget.src = notFoundImagePath;
            }}
        />
    );
}

const useQueryStringsDimensions = (src?: string, styleProps?: { width: string | number; height: string | number }) => {
    return React.useMemo(() => {
        const queryStrings = src && /\?.*/.exec(src);
        const noDimensions = {
            url: src,
            width: styleProps?.width,
            height: styleProps?.height,
        };

        if (queryStrings) {
            const parsedUrl = parseImageUrl(src);

            if (!parsedUrl.width || !parsedUrl.height) {
                return noDimensions;
            }
            return parsedUrl;
        }
        return noDimensions;
    }, [src]);
};

/**
 * XNG-Changes
 * "overflow: hidden" to prevent altText overlap
 */
const StyledImg = styled.img<InjectableCss>`
    overflow: hidden;
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    margin: auto;
    #alttext {
        ${ellipsis(3)}
    }

    ${injectCss}
`;

export default Img;
